import React, { useEffect, useMemo, useRef, useState } from 'react';
import { ArrowLeft, Check, ChevronDown, Search, X } from 'lucide-react';
import type { CurriculumMatrixCatalog } from '@interactive-room';

import type { RubricOaSelection } from '../../types/rubricDraft';

export interface RubricOaCatalogSource {
  subjectTag: string;
  subjectLabel: string;
  catalog: CurriculumMatrixCatalog;
}

type CatalogOa = RubricOaSelection & {
  summary: string;
  axisLabel: string;
  subjectKey: string;
};

interface RubricOaPickerProps {
  /** Catálogos por asignatura (1 = mono; 2+ = inter/multi). */
  sources: RubricOaCatalogSource[];
  catalogLoading: boolean;
  catalogError: string | null;
  selectedOas: RubricOaSelection[];
  onToggle: (oa: RubricOaSelection) => void;
  /** Compat: un solo catálogo sin metadatos de asignatura. */
  catalog?: CurriculumMatrixCatalog | null;
  subjectTag?: string;
  subjectLabel?: string;
}

function oaKey(oa: Pick<RubricOaSelection, 'code' | 'subjectTag'>): string {
  return `${oa.subjectTag || ''}::${oa.code}`;
}

export function RubricOaPicker({
  sources: sourcesProp,
  catalog,
  subjectTag = '',
  subjectLabel = '',
  catalogLoading,
  catalogError,
  selectedOas,
  onToggle,
}: RubricOaPickerProps) {
  const sources = useMemo((): RubricOaCatalogSource[] => {
    if (Array.isArray(sourcesProp) && sourcesProp.length > 0) return sourcesProp;
    if (catalog) {
      return [
        {
          subjectTag: subjectTag || 'general',
          subjectLabel: subjectLabel || 'Asignatura',
          catalog,
        },
      ];
    }
    return [];
  }, [sourcesProp, catalog, subjectTag, subjectLabel]);

  const multiSubject = sources.length > 1;
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [activeSubject, setActiveSubject] = useState<string | null>(null);
  const [activeAxis, setActiveAxis] = useState<string | null>(null);
  const rootRef = useRef<HTMLDivElement>(null);
  const searchRef = useRef<HTMLInputElement>(null);

  const selectedKeys = useMemo(
    () => new Set(selectedOas.map((oa) => oaKey(oa))),
    [selectedOas],
  );

  const catalogOas = useMemo((): CatalogOa[] => {
    return sources.flatMap((source) =>
      source.catalog.axes.flatMap((axis) =>
        axis.oas.map((oa) => ({
          code: oa.code,
          text: oa.text,
          axis: axis.id,
          summary: oa.summary,
          axisLabel: axis.label,
          subjectTag: source.subjectTag,
          subjectLabel: source.subjectLabel,
          subjectKey: source.subjectTag,
        })),
      ),
    );
  }, [sources]);

  const subjectOptions = useMemo(
    () =>
      sources.map((source) => ({
        id: source.subjectTag,
        label: source.subjectLabel,
        count: source.catalog.axes.reduce((sum, axis) => sum + axis.oas.length, 0),
        selected: selectedOas.filter((oa) => oa.subjectTag === source.subjectTag).length,
      })),
    [sources, selectedOas],
  );

  const axes = useMemo(() => {
    const source = sources.find((item) => item.subjectTag === activeSubject);
    if (!source) return [] as Array<{ id: string; label: string; count: number }>;
    return source.catalog.axes.map((axis) => ({
      id: axis.id,
      label: axis.label,
      count: axis.oas.length,
    }));
  }, [sources, activeSubject]);

  const selectedByAxis = useMemo(() => {
    const map = new Map<string, number>();
    for (const oa of selectedOas) {
      if (activeSubject && oa.subjectTag && oa.subjectTag !== activeSubject) continue;
      const key = oa.axis || '';
      map.set(key, (map.get(key) ?? 0) + 1);
    }
    return map;
  }, [selectedOas, activeSubject]);

  const activeAxisMeta = axes.find((axis) => axis.id === activeAxis) ?? null;
  const activeSubjectMeta = subjectOptions.find((item) => item.id === activeSubject) ?? null;

  const axisOas = useMemo(() => {
    if (!activeSubject || !activeAxis) return [] as CatalogOa[];
    const q = query.trim().toLowerCase();
    return catalogOas.filter((oa) => {
      if (oa.subjectKey !== activeSubject) return false;
      if (oa.axis !== activeAxis) return false;
      if (!q) return true;
      return (
        oa.code.toLowerCase().includes(q) ||
        oa.summary.toLowerCase().includes(q) ||
        oa.text.toLowerCase().includes(q)
      );
    });
  }, [activeSubject, activeAxis, catalogOas, query]);

  useEffect(() => {
    if (!open) return;
    const onPointer = (event: MouseEvent) => {
      if (!rootRef.current?.contains(event.target as Node)) {
        setOpen(false);
      }
    };
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        if (activeAxis) {
          setActiveAxis(null);
          setQuery('');
          return;
        }
        if (multiSubject && activeSubject) {
          setActiveSubject(null);
          return;
        }
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', onPointer);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onPointer);
      document.removeEventListener('keydown', onKey);
    };
  }, [open, activeAxis, activeSubject, multiSubject]);

  useEffect(() => {
    if (!open) {
      setQuery('');
      setActiveAxis(null);
      setActiveSubject(null);
      return;
    }
    if (!multiSubject && sources[0]) {
      setActiveSubject(sources[0].subjectTag);
    } else {
      setActiveSubject(null);
    }
    setActiveAxis(null);
    setQuery('');
  }, [open, multiSubject, sources]);

  useEffect(() => {
    if (!activeSubject || activeAxis) return;
    if (axes.length === 1) {
      setActiveAxis(axes[0].id);
    }
  }, [activeSubject, axes, activeAxis]);

  useEffect(() => {
    if (open && activeAxis) {
      const id = window.setTimeout(() => searchRef.current?.focus(), 0);
      return () => window.clearTimeout(id);
    }
    return undefined;
  }, [open, activeAxis]);

  const removeSelected = (oa: RubricOaSelection) => {
    if (selectedKeys.has(oaKey(oa))) onToggle(oa);
  };

  const step = !activeSubject
    ? 'subjects'
    : activeAxis
      ? 'oas'
      : 'categories';

  const triggerPrimary = catalogLoading
    ? 'Cargando catálogo…'
    : selectedOas.length === 0
      ? multiSubject
        ? 'Seleccionar OA por asignatura'
        : 'Seleccionar OA del currículum'
      : selectedOas.length === 1
        ? selectedOas[0].code
        : `${selectedOas.length} OA seleccionados`;

  const triggerSecondary =
    !catalogLoading && selectedOas.length === 1
      ? [
          selectedOas[0].subjectLabel,
          selectedOas[0].text.trim().slice(0, 64) +
            (selectedOas[0].text.trim().length > 64 ? '…' : ''),
        ]
          .filter(Boolean)
          .join(' · ')
      : !catalogLoading && selectedOas.length === 0
        ? multiSubject
          ? 'Elige asignatura → eje → objetivo'
          : 'Primero el eje, luego el objetivo'
        : null;

  return (
    <div className="rubric-oa" ref={rootRef}>
      {selectedOas.length > 0 ? (
        <ul className="rubric-oa__chips" aria-label="OA seleccionados">
          {selectedOas.map((oa) => (
            <li key={oaKey(oa)}>
              <button
                type="button"
                className="rubric-oa__chip"
                onClick={() => removeSelected(oa)}
                title={`${oa.subjectLabel ? `${oa.subjectLabel} · ` : ''}${oa.code}: quitar`}
              >
                <span className="rubric-oa__chip-code">
                  {oa.subjectLabel && multiSubject ? `${oa.subjectLabel}: ` : ''}
                  {oa.code}
                </span>
                <X className="h-3 w-3 opacity-70" aria-hidden />
              </button>
            </li>
          ))}
        </ul>
      ) : null}

      <button
        type="button"
        className={`rubric-oa__trigger${open ? ' rubric-oa__trigger--open' : ''}${
          selectedOas.length > 0 ? ' rubric-oa__trigger--filled' : ''
        }`}
        aria-expanded={open}
        aria-haspopup="dialog"
        onClick={() => setOpen((value) => !value)}
        disabled={catalogLoading || (sources.length === 0 && !catalogError)}
      >
        <span className="rubric-oa__trigger-copy min-w-0 flex-1 text-left">
          <span className="rubric-oa__trigger-primary">{triggerPrimary}</span>
          {triggerSecondary ? (
            <span className="rubric-oa__trigger-secondary">{triggerSecondary}</span>
          ) : null}
        </span>
        <ChevronDown
          className={`rubric-oa__trigger-icon h-4 w-4 shrink-0 transition ${
            open ? 'rotate-180' : ''
          }`}
          aria-hidden
        />
      </button>

      {catalogError ? <p className="rubric-oa__error">{catalogError}</p> : null}

      {open && sources.length > 0 ? (
        <div className="rubric-oa__panel" role="dialog" aria-label="Selector de OA">
          {step === 'subjects' ? (
            <>
              <p className="rubric-oa__step-label">Asignatura</p>
              <ul className="rubric-oa__categories">
                {subjectOptions.map((subject) => (
                  <li key={subject.id}>
                    <button
                      type="button"
                      className="rubric-oa__category"
                      onClick={() => {
                        setActiveSubject(subject.id);
                        setActiveAxis(null);
                        setQuery('');
                      }}
                    >
                      <span className="min-w-0 flex-1 text-left">
                        <span className="rubric-oa__category-name">{subject.label}</span>
                        <span className="rubric-oa__category-meta">
                          {subject.count} OA
                          {subject.selected > 0
                            ? ` · ${subject.selected} seleccionado${subject.selected === 1 ? '' : 's'}`
                            : ''}
                        </span>
                      </span>
                      <ChevronDown className="h-4 w-4 shrink-0 -rotate-90 opacity-45" aria-hidden />
                    </button>
                  </li>
                ))}
              </ul>
            </>
          ) : null}

          {step === 'categories' ? (
            <>
              <div className="rubric-oa__step-bar">
                {multiSubject ? (
                  <button
                    type="button"
                    className="rubric-oa__back"
                    onClick={() => {
                      setActiveSubject(null);
                      setActiveAxis(null);
                      setQuery('');
                    }}
                  >
                    <ArrowLeft className="h-3.5 w-3.5" aria-hidden />
                    Volver
                  </button>
                ) : (
                  <span className="rubric-oa__step-label">Eje o categoría</span>
                )}
                <p className="rubric-oa__step-current truncate" title={activeSubjectMeta?.label}>
                  {activeSubjectMeta?.label}
                </p>
              </div>
              <ul className="rubric-oa__categories">
                {axes.map((axis) => {
                  const picked = selectedByAxis.get(axis.id) ?? 0;
                  return (
                    <li key={axis.id}>
                      <button
                        type="button"
                        className="rubric-oa__category"
                        onClick={() => {
                          setActiveAxis(axis.id);
                          setQuery('');
                        }}
                      >
                        <span className="min-w-0 flex-1 text-left">
                          <span className="rubric-oa__category-name">{axis.label}</span>
                          <span className="rubric-oa__category-meta">
                            {axis.count} OA
                            {picked > 0
                              ? ` · ${picked} seleccionado${picked === 1 ? '' : 's'}`
                              : ''}
                          </span>
                        </span>
                        <ChevronDown className="h-4 w-4 shrink-0 -rotate-90 opacity-45" aria-hidden />
                      </button>
                    </li>
                  );
                })}
              </ul>
            </>
          ) : null}

          {step === 'oas' ? (
            <>
              <div className="rubric-oa__step-bar">
                <button
                  type="button"
                  className="rubric-oa__back"
                  onClick={() => {
                    setActiveAxis(null);
                    setQuery('');
                  }}
                >
                  <ArrowLeft className="h-3.5 w-3.5" aria-hidden />
                  Volver
                </button>
                <p className="rubric-oa__step-current truncate" title={activeAxisMeta?.label}>
                  {[activeSubjectMeta?.label, activeAxisMeta?.label].filter(Boolean).join(' · ')}
                </p>
              </div>

              <label className="rubric-oa__search">
                <Search className="h-3.5 w-3.5 shrink-0 opacity-60" aria-hidden />
                <input
                  ref={searchRef}
                  type="search"
                  value={query}
                  onChange={(event) => setQuery(event.target.value)}
                  placeholder="Buscar por código o texto…"
                  autoComplete="off"
                />
              </label>

              <div className="rubric-oa__list" role="listbox" aria-multiselectable="true">
                {axisOas.length === 0 ? (
                  <p className="rubric-oa__none">No hay OA que coincidan en esta categoría.</p>
                ) : (
                  <ul>
                    {axisOas.map((oa) => {
                      const selected = selectedKeys.has(oaKey(oa));
                      return (
                        <li key={`${oa.subjectKey}-${oa.axis}-${oa.code}`}>
                          <button
                            type="button"
                            role="option"
                            aria-selected={selected}
                            className={`rubric-oa__option${
                              selected ? ' rubric-oa__option--selected' : ''
                            }`}
                            onClick={() =>
                              onToggle({
                                code: oa.code,
                                text: oa.text,
                                axis: oa.axis,
                                subjectTag: oa.subjectTag,
                                subjectLabel: oa.subjectLabel,
                              })
                            }
                          >
                            <span className="rubric-oa__check" aria-hidden>
                              {selected ? <Check className="h-3 w-3" strokeWidth={3} /> : null}
                            </span>
                            <span className="min-w-0">
                              <span className="rubric-oa__option-code">{oa.code}</span>
                              <span className="rubric-oa__option-summary">{oa.summary}</span>
                            </span>
                          </button>
                        </li>
                      );
                    })}
                  </ul>
                )}
              </div>
            </>
          ) : null}
        </div>
      ) : null}
    </div>
  );
}
